// store/modules/wooAnalytics.js
import axios from "axios";

const state = {
  revenueData: [],
  orders: [],
  allOrders: [],
  products: [],
  loading: false,
  error: null,
  selectedProduct: '', // Add this to track selected product filter
  
  ordersPagination: {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0
  }
};

const getters = {
  getRevenueData: state => state.revenueData,
  
  // This getter will return filtered orders based on selectedProduct
  getOrders: state => {
    if (!state.selectedProduct) {
      return state.allOrders;
    }
    
    return state.allOrders.filter(order => {
      if (!order.products) return false;
      
      // Handle both string and array formats for products
      if (typeof order.products === 'string') {
        console.log(state.selectedProduct)
        return order.products.toLowerCase().includes(state.selectedProduct.toLowerCase());
      }
      
      if (Array.isArray(order.products)) {
        return order.products.some(product => 
          product.name && product.name.toLowerCase().includes(state.selectedProduct.toLowerCase())
        );
      }
      
      return false;
    });
  },
  
  getAllOrders: state => state.allOrders,
  getProducts: state => state.products,
  isLoading: state => state.loading,
  getError: state => state.error,
  getOrdersPagination: state => state.ordersPagination,
  getSelectedProduct: state => state.selectedProduct,
  
  // Updated to use the filtered orders from getOrders getter
  getFilteredOrders: (state, getters) => getters.getOrders,
  
  getPaginatedOrders: (state, getters) => {
    const filteredOrders = getters.getOrders;
    const start = (state.ordersPagination.currentPage - 1) * state.ordersPagination.itemsPerPage;
    const end = start + state.ordersPagination.itemsPerPage;
    return filteredOrders.slice(start, end);
  }
};

const actions = {
  async fetchAnalyticsData({ commit }, { startDate, endDate }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const params = {
        start: formatDateForAPI(startDate),
        end: formatDateForAPI(endDate),
      };
      
      const [revenueRes, ordersRes, productsRes] = await Promise.all([
        axios.get("/v1/woo/revenue", { params }),
        axios.get("/v1/woo/orders", { params }),
        axios.get("/v1/woo/products", { params })
      ]);
      
      commit('setRevenueData', revenueRes.data || []);
      commit('setAllOrders', ordersRes.data || []);
      commit('setProducts', productsRes.data || []);
      commit('setOrdersPaginationTotal', ordersRes.data && ordersRes.data.length || 0);

      
      return true;
    } catch (error) {
      commit('setError', error);
      console.error("Error fetching analytics data:", error);
      return false;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Simplified action - just updates the filter, getter handles the filtering
  filterOrdersByProduct({ commit }, productName) {
    commit('setSelectedProduct', productName || '');
    commit('setOrdersCurrentPage', 1); // Reset to first page when filtering
  },
  
  setOrdersPage({ commit }, page) {
    commit('setOrdersCurrentPage', page);
  },
  
  setOrdersPerPage({ commit }, perPage) {
    commit('setOrdersItemsPerPage', perPage);
  }
};

const mutations = {
  setRevenueData(state, data) {
    state.revenueData = data;
  },
  
  setAllOrders(state, orders) {
    state.allOrders = orders;
  },
  
  setProducts(state, products) {
    state.products = products;
  },
  
  setSelectedProduct(state, product) {
    state.selectedProduct = product;
  },
  
  setLoading(state, isLoading) {
    state.loading = isLoading;
  },
  
  setError(state, error) {
    state.error = (error && error.message) ? error.message : 'An error occurred';
  },
  
  clearError(state) {
    state.error = null;
  },
  
  setOrdersCurrentPage(state, page) {
    state.ordersPagination.currentPage = page;
  },
  
  setOrdersItemsPerPage(state, perPage) {
    state.ordersPagination.itemsPerPage = perPage;
    state.ordersPagination.currentPage = 1; // Reset to first page
  },
  
  setOrdersPaginationTotal(state, total) {
    state.ordersPagination.totalItems = total;
  }
};

// Helper function
function formatDateForAPI(date) {
  return date.toISOString().split('T')[0];
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};